import * as React from "react";
import { Box } from "@mui/material";
import { AUTH_INPUT_BG, AUTH_INPUT_BORDER, AUTH_TEXT_PRIMARY, BRAND_BLUE, AUTH_ERROR } from "../theme/appTheme";

const boxSx = {
  width: 44,
  height: 52,
  borderRadius: 1.5,
  textAlign: "center",
  fontSize: "1.375rem",
  fontWeight: 600,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  border: `1px solid ${AUTH_INPUT_BORDER}`,
  outline: "none",
  color: AUTH_TEXT_PRIMARY,
  background: AUTH_INPUT_BG,
};

// Six-box code UI for the emailed login / registration / reset codes:
// - editable (default): types in the code, auto-advancing focus.
// - readOnly: display-only variant.
function OtpBoxes({ length = 6, value = "", onChange, readOnly = false, error = false, autoFocus = true }) {
  const inputRefs = React.useRef([]);
  // Empty boxes are carried as spaces in `value` so clearing a middle digit
  // doesn't shift the later ones left (trailing blanks are trimmed).
  const digits = React.useMemo(() => {
    const arr = value.split("").slice(0, length).map((d) => (d === " " ? "" : d));
    while (arr.length < length) arr.push("");
    return arr;
  }, [value, length]);

  const focusBox = (index) => {
    const el = inputRefs.current[index];
    if (el) el.focus();
  };

  const emitChange = (nextDigits) => {
    onChange?.(nextDigits.map((d) => d || " ").join("").trimEnd());
  };

  const handleChange = (index, rawInput) => {
    const char = rawInput.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = char;
    emitChange(next);
    if (char && index < length - 1) focusBox(index + 1);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      focusBox(index - 1);
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusBox(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      focusBox(index + 1);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.split("");
    while (next.length < length) next.push("");
    emitChange(next);
    focusBox(Math.min(pasted.length, length - 1));
  };

  return (
    <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }} onPaste={!readOnly ? handlePaste : undefined}>
      {digits.map((digit, index) =>
        readOnly ? (
          <Box
            key={index}
            sx={{
              ...boxSx,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {digit}
          </Box>
        ) : (
          <Box
            key={index}
            component="input"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            autoFocus={autoFocus && index === 0}
            ref={(el) => (inputRefs.current[index] = el)}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            sx={{
              ...boxSx,
              borderColor: error ? AUTH_ERROR : AUTH_INPUT_BORDER,
              "&:focus": { borderColor: BRAND_BLUE, borderWidth: 2 },
            }}
          />
        )
      )}
    </Box>
  );
}

export default OtpBoxes;
